import { Loader2, Plus, Save } from "lucide-react";
import React, { useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Button } from "../../../../components/button";
import FormHeader from "../../../../components/form/FormHeader";
import { useToast } from "../../../../components/toast-container";
import { BoostVisibilitySection } from "../components/editor/BoostVisibilitySection";
import { CostSummarySection } from "../components/editor/CostSummarySection";
import { CouponDetailsSection } from "../components/editor/CouponDetailsSection";
import type { PlanType } from "../components/editor/CouponSettingsSection";
import { CouponSettingsSection } from "../components/editor/CouponSettingsSection";
import { MinimumPurchaseSection } from "../components/editor/MinimumPurchaseSection";
import { TermsConditionsSection } from "../components/editor/TermsConditionsSection";
import { TimeBasedAvailabilitySection } from "../components/editor/TimeBasedAvailabilitySection";
import { UsageLimitsSection } from "../components/editor/UsageLimitsSection";
import { MERCHANT_COUPON_NOTIFICATION_MESSAGES } from "../data/merchantCouponEditor.constants";
import type { MerchantCouponUIState } from "../data/merchantCouponEditor.data";
import { getFirstMerchantCouponApiErrorMessage } from "../utils/merchantCouponApiError.utils";
import {
  useCreateMerchantCoupon,
  useGetMerchantCouponDetail,
  useMerchantCouponEditorForm,
  useUpdateMerchantCoupon,
} from "../hooks";

const MerchantCouponEditorPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const { addToast } = useToast();
  const copyFromId = searchParams.get("copyFrom");
  const isEditMode = !!id;
  const isCopyMode = !isEditMode && !!copyFromId;
  const detailId = id ?? copyFromId ?? undefined;

  const {
    coupon,
    updateField,
    addTerm,
    removeTerm,
    updateTerm,
    resetCoupon,
    loadCoupon,
    validationErrors,
    validateCoupon,
  } =
    useMerchantCouponEditorForm();
  const {
    coupon: detailCoupon,
    loading: isLoading,
    error: detailError,
  } = useGetMerchantCouponDetail(detailId);
  const { createMerchantCoupon, loading: isCreating } = useCreateMerchantCoupon();
  const { updateMerchantCoupon, loading: isUpdating } = useUpdateMerchantCoupon();

  const planType: PlanType = "premium";

  useEffect(() => {
    if (detailCoupon) {
      loadCoupon(isCopyMode ? { ...detailCoupon, id: "" } : detailCoupon);
    }
  }, [detailCoupon, isCopyMode, loadCoupon]);

  useEffect(() => {
    if (!isEditMode && !copyFromId) {
      resetCoupon();
    }
  }, [isEditMode, copyFromId, resetCoupon]);

  useEffect(() => {
    if (detailError) {
      addToast({
        type: "error",
        title: "Error",
        message: detailError,
      });
    }
  }, [detailError, addToast]);

  const handleCreate = async () => {
    const validation = validateCoupon();
    if (!validation.isValid) {
      addToast({
        type: "error",
        title: "Error",
        message: validation.firstError ?? MERCHANT_COUPON_NOTIFICATION_MESSAGES.CREATE_ERROR,
      });
      return;
    }

    try {
      await createMerchantCoupon(coupon);
      addToast({
        type: "success",
        title: "Success",
        message: MERCHANT_COUPON_NOTIFICATION_MESSAGES.CREATE_SUCCESS,
      });
      navigate("/merchant/coupons");
    } catch (createError: unknown) {
      addToast({
        type: "error",
        title: "Error",
        message: getFirstMerchantCouponApiErrorMessage(
          createError,
          MERCHANT_COUPON_NOTIFICATION_MESSAGES.CREATE_ERROR,
        ),
      });
    }
  };

  const handleUpdate = async () => {
    if (!id) return;
    const validation = validateCoupon();
    if (!validation.isValid) {
      addToast({
        type: "error",
        title: "Error",
        message: validation.firstError ?? MERCHANT_COUPON_NOTIFICATION_MESSAGES.UPDATE_ERROR,
      });
      return;
    }

    try {
      await updateMerchantCoupon(id, coupon);
      addToast({
        type: "success",
        title: "Success",
        message: MERCHANT_COUPON_NOTIFICATION_MESSAGES.UPDATE_SUCCESS,
      });
      navigate("/merchant/coupons");
    } catch (updateError: unknown) {
      addToast({
        type: "error",
        title: "Error",
        message: getFirstMerchantCouponApiErrorMessage(
          updateError,
          MERCHANT_COUPON_NOTIFICATION_MESSAGES.UPDATE_ERROR,
        ),
      });
    }
  };

  const handleFieldChange = (
    field: keyof MerchantCouponUIState,
    value: MerchantCouponUIState[keyof MerchantCouponUIState],
  ) => {
    updateField(field, value);
  };

  const handleAddTerm = () => {
    addTerm();
  };

  const handleRemoveTerm = (index: number) => {
    removeTerm(index);
  };

  const handleUpdateTerm = (index: number, value: string) => {
    updateTerm(index, value);
  };

  return (
    <div className="bg-[rgba(241,245,249,0.5)] dark:bg-gray-900/50 min-h-screen">
      <FormHeader
        onBack={() => navigate(-1)}
        title={isEditMode ? "Edit Coupon" : "Create New Coupon"}
        subtitle={
          isEditMode
            ? "Update coupon details and settings"
            : "Create a coupon to boost sales and reward customers"
        }
        actions={
          isEditMode ? (
            <Button
              onClick={handleUpdate}
              disabled={isUpdating}
              className="flex h-[40px] items-center gap-2 rounded-[8px] bg-[#00bc7d] px-4 py-2 text-[14px] font-semibold leading-[20px] text-white transition-colors hover:bg-[#00a86d] dark:bg-emerald-500 dark:hover:bg-emerald-600"
            >
              {isUpdating ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <Save className="h-4 w-4" aria-hidden="true" />
              )}
              {isUpdating
                ? "Updating..."
                : coupon.visibility_boost_48h
                  ? "Update Coupon & Purchase Boost"
                  : "Update Coupon"}
            </Button>
          ) : (
            <Button
              onClick={handleCreate}
              disabled={isCreating}
              className="flex h-[40px] items-center gap-2 rounded-[8px] bg-[#00bc7d] px-4 py-2 text-[14px] font-semibold leading-[20px] text-white transition-colors hover:bg-[#00a86d] dark:bg-emerald-500 dark:hover:bg-emerald-600"
            >
              {isCreating ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <Plus className="h-4 w-4" aria-hidden="true" />
              )}
              {isCreating
                ? "Creating..."
                : coupon.visibility_boost_48h
                  ? "Create Coupon & Purchase Boost"
                  : "Create Coupon"}
            </Button>
          )
        }
      />
      {/* Main Content */}
      <div className="p-[30px]">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <p className="text-gray-500 dark:text-gray-400">
              {isCopyMode ? "Loading coupon to copy..." : "Loading coupon details..."}
            </p>
          </div>
        ) : (
          <div className="space-y-6 max-w-full">
            <CouponDetailsSection
              coupon={coupon}
              onFieldChange={handleFieldChange}
              isCreating={isCreating || isUpdating}
              errors={validationErrors}
            />

            <UsageLimitsSection
              coupon={coupon}
              onFieldChange={handleFieldChange}
              isCreating={isCreating || isUpdating}
              errors={validationErrors}
            />

            <CouponSettingsSection
              coupon={coupon}
              onFieldChange={handleFieldChange}
              isCreating={isCreating || isUpdating}
              planType={planType}
              errors={validationErrors}
            />

            <TermsConditionsSection
              coupon={coupon}
              onAddTerm={handleAddTerm}
              onRemoveTerm={handleRemoveTerm}
              onUpdateTerm={handleUpdateTerm}
              isCreating={isCreating || isUpdating}
              errors={validationErrors}
            />

            <CostSummarySection coupon={coupon} />
          </div>
        )}
      </div>
    </div>
  );
};

export default MerchantCouponEditorPage;
